import React, { useState, useEffect } from 'react';
import { Calendar, TrendingUp, Plus } from 'lucide-react';
import api from './api';
import CalendarView from './components/CalendarView';
import ProgressView from './components/ProgressView';
import AddSessionModal from './components/AddSessionModal';
import AddExerciseModal from './components/AddExerciseModal';

function App() {
  const [sessions, setSessions] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [view, setView] = useState('calendar');
  const [showAddSession, setShowAddSession] = useState(false);
  const [showAddExercise, setShowAddExercise] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [exercisesData, sessionsData] = await Promise.all([
        api.getExercises(),
        api.getSessions()
      ]);
      setExercises(exercisesData);
      setSessions(sessionsData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const addSession = async (session) => {
    try {
      await api.addSession(session);
      loadData();
    } catch (error) {
      console.error('Error adding session:', error);
    }
  };

  const deleteSession = async (id) => {
    try {
      await api.deleteSession(id);
      loadData();
    } catch (error) {
      console.error('Error deleting session:', error);
    }
  };

  const addExercise = async (exercise) => {
    try {
      await api.addExercise(exercise);
      loadData();
    } catch (error) {
      console.error('Error adding exercise:', error);
    }
  };

  const updateExercise = async (id, exercise) => {
    try {
      await api.updateExercise(id, exercise);
      loadData();
    } catch (error) {
      console.error('Error updating exercise:', error);
    }
  };

  const deleteExercise = async (id) => {
    try {
      await api.deleteExercise(id);
      loadData();
    } catch (error) {
      console.error('Error deleting exercise:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white flex items-center justify-center">
        <div className="text-2xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white p-4">
      <div className="max-w-7xl mx-auto">
        <header className="mb-8">
          <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
            Training Tracker
          </h1>
          <p className="text-slate-300">Track your strength and cardio training progress</p>
        </header>

        <div className="flex gap-4 mb-6 flex-wrap">
          <button
            onClick={() => setView('calendar')}
            className={`px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-all ${
              view === 'calendar' 
                ? 'bg-blue-600 shadow-lg shadow-blue-500/50' 
                : 'bg-slate-700 hover:bg-slate-600'
            }`}
          >
            <Calendar className="w-5 h-5" />
            Calendar
          </button>
          <button
            onClick={() => setView('progress')}
            className={`px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-all ${
              view === 'progress' 
                ? 'bg-blue-600 shadow-lg shadow-blue-500/50' 
                : 'bg-slate-700 hover:bg-slate-600'
            }`}
          >
            <TrendingUp className="w-5 h-5" />
            Progress
          </button>
          <button
            onClick={() => setShowAddSession(true)}
            className="px-6 py-3 rounded-lg font-semibold flex items-center gap-2 bg-green-600 hover:bg-green-700 transition-all shadow-lg shadow-green-500/50"
          >
            <Plus className="w-5 h-5" />
            Add Session
          </button>
          <button
            onClick={() => setShowAddExercise(true)}
            className="px-6 py-3 rounded-lg font-semibold flex items-center gap-2 bg-purple-600 hover:bg-purple-700 transition-all shadow-lg shadow-purple-500/50"
          >
            <Plus className="w-5 h-5" />
            Manage Exercises
          </button>
        </div>

        {view === 'calendar' && (
          <CalendarView 
            sessions={sessions} 
            exercises={exercises}
            onSelectDate={setSelectedDate}
            selectedDate={selectedDate}
            onDeleteSession={deleteSession}
          />
        )}

        {view === 'progress' && (
          <ProgressView sessions={sessions} exercises={exercises} />
        )}

        {showAddSession && (
          <AddSessionModal
            exercises={exercises}
            onAdd={addSession}
            onClose={() => setShowAddSession(false)}
          />
        )}

        {showAddExercise && (
          <AddExerciseModal
            exercises={exercises}
            onAdd={addExercise}
            onUpdate={updateExercise}
            onDelete={deleteExercise}
            onClose={() => setShowAddExercise(false)}
          />
        )}
      </div>
    </div>
  );
}

export default App;
